import React, { useCallback } from "react"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { getBuildingTypeIcon } from "shared/lib/building-references/getBuildingTypeIcon"
import { useBuildingTypeOptions } from "shared/lib/building-references/useBuildingTypeOptions"
import { SelectOption } from "shared/types/Select"
import { WithClassName } from "shared/types/WithClassName"
import { BuildingType } from "shared/types/references/BuildingType"
import { Icon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"
import { Combobox } from "shared/ui/input/combobox/Combobox"
import { MediaOption } from "shared/ui/media-option/MediaOption"

export interface BuildingTypeInputProps extends WithClassName {
  name?: string
  loading?: boolean
  disabled?: boolean
  elevated?: boolean
}

export const BuildingTypeInput = React.memo(
  ({
    name = "buildingTypes",
    loading,
    disabled,
    elevated,
    className
  }: BuildingTypeInputProps) => {
    const { t } = useTranslation()
    const options = useBuildingTypeOptions()

    const renderValue = useCallback(
      () => (
        <span className="combobox__placeholder">{t("General.Select")}</span>
      ),
      [t]
    )

    return (
      <Combobox.Root
        name={name}
        label={t("BuildingReference.Filter.BuildingTypes")}
        loading={loading}
        disabled={disabled}
        elevated={elevated}
        className={classNames("building-type-input", className)}
        items={options}
        multiple
      >
        <Combobox.Label />
        <Combobox.Control>
          <Combobox.Trigger>
            <Combobox.Value>{renderValue}</Combobox.Value>
            <Combobox.Icon />
          </Combobox.Trigger>
          <Combobox.Portal>
            <Combobox.Positioner align="start" sideOffset={8}>
              <Combobox.Popup
                aria-label={t("BuildingReference.Filter.BuildingTypes")}
              >
                <Combobox.InputContainer>
                  <Combobox.Input />
                  <Icon name={IconName.SEARCH} />
                </Combobox.InputContainer>
                <Combobox.Empty />
                <Combobox.List>
                  {(option: SelectOption<BuildingType>) => (
                    <Combobox.Item key={option.value} value={option}>
                      <Combobox.ItemIndicator />
                      <Combobox.ItemContent>
                        <MediaOption.Root variant="inline">
                          <MediaOption.Media>
                            <Icon name={getBuildingTypeIcon(option.value)} />
                          </MediaOption.Media>
                          <MediaOption.Label>{option.label}</MediaOption.Label>
                        </MediaOption.Root>
                      </Combobox.ItemContent>
                    </Combobox.Item>
                  )}
                </Combobox.List>
              </Combobox.Popup>
            </Combobox.Positioner>
          </Combobox.Portal>
        </Combobox.Control>
      </Combobox.Root>
    )
  }
)

BuildingTypeInput.displayName = "BuildingTypeInput"
